// TemplatesBoard.tsx
// Template management view

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, ListChecks, TestTube2, Save } from "lucide-react";
import { PhonePreviewIOS, PhonePreviewAndroid } from "~/components/pages/PhonePreview";
import { trpc } from "~/api/trpc";
import { asHttpImageOrNull } from "~/components/pages/utils";

interface TemplatesBoardProps {
  onTestSend?: any;
  onSendTest?: any;
}

export function TemplatesBoard(props: TemplatesBoardProps) {
  const { onTestSend, onSendTest } = props;
  
  // TRPC queries and mutations
  const { data: templatesData, refetch } = trpc.pushNotifications.listPushNotificationTemplates.useQuery();
  const createTemplateMutation = trpc.pushNotifications.createPushNotificationTemplate.useMutation();
  const updateTemplateMutation = trpc.pushNotifications.updatePushNotificationTemplate.useMutation();
  const deleteTemplateMutation = trpc.pushNotifications.deletePushNotificationTemplate.useMutation();
  const sendTestMutation = trpc.pushNotifications.sendTestPushNotification.useMutation();

  const templates = templatesData ?? [];
  
  // Debug: Log templates when they load
  React.useEffect(() => {
    if (templatesData && templatesData.length > 0) {
      console.log("=== LOADED TEMPLATES ===");
      console.log("First template:", JSON.stringify(templatesData[0], null, 2));
      console.log("=======================");
    }
  }, [templatesData]);
  const callTest = onTestSend ?? onSendTest ?? (async (input: any) => sendTestMutation.mutateAsync(input));

  const [selected, setSelected] = useState<any | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  const startCreate = () => {
    setSelected(null);
    setOpenModal(true);
  };

  const startEdit = (tpl: any) => {
    console.log("=== EDITING TEMPLATE ===");
    console.log("Template data:", JSON.stringify(tpl, null, 2));
    console.log("=======================");
    setSelected(tpl);
    setOpenModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this template?")) return;
    try {
      await deleteTemplateMutation.mutateAsync({ id });
      await refetch();
      if (selected?.id === id) setSelected(null);
    } catch (e: any) {
      alert(e?.message || "Failed to delete template");
    }
  };

  const handleTestTemplate = async () => {
    if (!selected) return alert("Select a template first.");
    if (!testEmail) return alert("Enter a test email.");
    const imgUrl = asHttpImageOrNull((selected?.data as any)?.image);
    
    try {
      // Build sound configuration
      let soundConfig: any = undefined;
      if (selected?.data?.sound) {
        soundConfig = selected.data.sound;
      }
      
      const input = {
        email: testEmail,
        notification: {
          // Platform-specific fields - the router will handle splitting these
          androidTitle: selected.androidTitle,
          androidBody: selected.androidBody,
          iosTitle: selected.iosTitle,
          iosSubtitle: selected.iosSubtitle ?? undefined,
          iosBody: selected.iosBody,
          richContent: imgUrl ? { image: imgUrl } : undefined,
          mutableContent: selected?.data?.mutableContent ?? !!imgUrl,
          data: { 
            templateId: selected.id,
            // Add all template data for debugging
            ...selected.data 
          },
          // Advanced fields from template
          priority: selected?.data?.priority ?? undefined,
          interruptionLevel: selected?.data?.interruptionLevel ?? undefined,
          badge: selected?.data?.badge ?? undefined,
          ttl: selected?.data?.ttl ?? undefined,
          channelId: selected?.data?.channelId ?? undefined,
          categoryId: selected?.data?.categoryId ?? undefined,
          icon: selected?.data?.icon ?? undefined,
          sound: soundConfig,
          _contentAvailable: selected?.data?._contentAvailable ?? undefined,
        },
        campaignId: `template_test_${selected.id}_${Date.now()}`,
        mergeVariables: {},
      } as const;
      
      // LOG THE PAYLOAD - EXPANDED
      console.log("=== TEST PUSH NOTIFICATION PAYLOAD ===");
      console.log("Selected Template Data:", selected.data);
      console.log("\nFull Input:");
      console.log(JSON.stringify(input, null, 2));
      console.log("\nNotification Object Only:");
      console.log(JSON.stringify(input.notification, null, 2));
      console.log("======================================");
      
      await callTest(input);
      alert("Test push triggered. Check console for detailed payload!");
    } catch (e: any) {
      console.error("Test failed:", e);
      alert(e?.message || "Test failed");
    }
  };

  const selectedPreviewImage = selected?.data?.image
    ? { name: "template-image", dataUrl: String(selected.data.image) }
    : null;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:auto-rows-min">
      <div className="lg:col-span-7 space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {templates.length} templates
          </div>
          <Button onClick={startCreate} className="rounded-xl">
            <Plus className="mr-2 h-4 w-4" /> New template
          </Button>
        </div>

        <Card className="rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <ListChecks className="h-4 w-4" /> Templates
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {templates.length === 0 && (
              <div className="text-sm text-muted-foreground">No templates yet.</div>
            )}
            <div className="divide-y rounded-xl border">
              {templates.map((tpl) => (
                <div
                  key={tpl.id}
                  className="flex items-start justify-between gap-3 px-3 py-3"
                >
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => setSelected(tpl)}
                  >
                    <div className="text-sm font-medium">{tpl.name}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      <div className="whitespace-normal break-words">
                        <span className="font-semibold">iOS:</span> {tpl.iosTitle}
                        {tpl.iosSubtitle ? ` — ${tpl.iosSubtitle}` : ""}
                      </div>
                      <div className="whitespace-normal break-words mt-0.5">
                        <span className="font-semibold">Android:</span> {tpl.androidTitle}
                      </div>
                    </div>
                  </button>
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-xl"
                      onClick={() => startEdit(tpl)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="rounded-xl"
                      onClick={() => handleDelete(tpl.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-5 lg:row-span-2 self-start">
        <Card className="rounded-2xl h-full">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {selected ? selected.name : "Preview"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!selected ? (
              <div className="text-sm text-muted-foreground">
                Select a template to see details.
              </div>
            ) : (
              <>
                {/* Dual Preview Section - Vertical Layout */}
                <div className="space-y-4">
                  <div className="text-xs text-muted-foreground text-center">Live Previews</div>
                  
                  {/* iOS Preview */}
                  <div className="space-y-2">
                    <div className="text-xs font-semibold text-blue-600 text-center">iOS</div>
                    <div className="flex justify-center">
                      <PhonePreviewIOS
                        appName="Your App"
                        header={selected.iosTitle}
                        subtitle={selected.iosSubtitle}
                        body={selected.iosBody}
                        link={undefined}
                        image={selectedPreviewImage}
                      />
                    </div>
                  </div>

                  {/* Android Preview */}
                  <div className="space-y-2">
                    <div className="text-xs font-semibold text-green-600 text-center">Android</div>
                    <div className="flex justify-center">
                      <PhonePreviewAndroid
                        appName="Your App"
                        title={selected.androidTitle}
                        body={selected.androidBody}
                        image={selectedPreviewImage}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-2 flex items-center gap-2">
                  <Input
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test@example.com"
                    className="h-9 w-56 rounded-xl"
                  />
                  <Button
                    variant="outline"
                    className="rounded-xl"
                    onClick={handleTestTemplate}
                  >
                    <TestTube2 className="mr-2 h-4 w-4" /> Test
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <TemplateModal
        open={openModal}
        onOpenChange={setOpenModal}
        initial={selected}
        onSaved={async () => {
          const result = await refetch();
          // Update selected template with fresh data after refetch
          if (selected && result.data) {
            const updatedTemplate = result.data.find((t: any) => t.id === selected.id);
            if (updatedTemplate) {
              setSelected(updatedTemplate);
            }
          }
        }}
        onCreate={async (input) => {
          // Send only new fields
          await createTemplateMutation.mutateAsync(input as any);
        }}
        onUpdate={async (input) => {
          console.log("=== CALLING UPDATE MUTATION ===");
          console.log("Input to mutation:", JSON.stringify(input, null, 2));
          // Send only new fields
          const result = await updateTemplateMutation.mutateAsync(input as any);
          console.log("Mutation result:", JSON.stringify(result, null, 2));
          console.log("===============================");
          return result;
        }}
      />
    </div>
  );
}

function TemplateModal({
  open,
  onOpenChange,
  initial,
  onSaved,
  onCreate,
  onUpdate,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initial: any | null;
  onSaved: () => Promise<void> | void;
  onCreate: (input: {
    name: string;
    androidTitle: string;
    androidBody: string;
    iosTitle: string;
    iosSubtitle: string;
    iosBody: string;
    data?: Record<string, unknown>;
  }) => Promise<any>;
  onUpdate: (input: {
    id: string;
    name?: string;
    androidTitle?: string;
    androidBody?: string;
    iosTitle?: string;
    iosSubtitle?: string;
    iosBody?: string;
    data?: Record<string, unknown>;
  }) => Promise<any>;
}) {
  const isEdit = !!initial;

  const [name, setName] = useState("");
  
  // Android fields
  const [androidTitle, setAndroidTitle] = useState("");
  const [androidBody, setAndroidBody] = useState("");
  
  // iOS fields
  const [iosTitle, setIosTitle] = useState("");
  const [iosSubtitle, setIosSubtitle] = useState("");
  const [iosBody, setIosBody] = useState("");
  
  // Shared
  const [imageUrl, setImageUrl] = useState("");

  useEffect(() => {
    if (open) {
      setName(initial?.name ?? "");
      
      // Use new fields if available, fallback to old fields
      setAndroidTitle(initial?.androidTitle ?? initial?.title ?? "");
      setAndroidBody(initial?.androidBody ?? initial?.body ?? "");
      
      setIosTitle(initial?.iosTitle ?? initial?.title ?? "");
      setIosSubtitle(initial?.iosSubtitle ?? initial?.subtitle ?? "");
      setIosBody(initial?.iosBody ?? initial?.body ?? "");
      
      // Safe access to data.image
      let imageValue = "";
      if (initial?.data && typeof initial.data === 'object' && 'image' in initial.data) {
        const img = (initial.data as any).image;
        imageValue = typeof img === "string" ? img : "";
      }
      setImageUrl(imageValue);
    }
  }, [open, initial]);

  const doSave = async () => {
    try {
      const imageValue = imageUrl?.trim() ? imageUrl.trim() : null;
      
      const data: Record<string, unknown> = {
        image: imageValue,
      };
      
      const payload = {
        name,
        androidTitle,
        androidBody,
        iosTitle,
        iosSubtitle,
        iosBody,
        data,
      };
      
      console.log("=== SAVING TEMPLATE ===");
      console.log("Is Edit:", isEdit);
      console.log("Payload:", JSON.stringify(payload, null, 2));
      console.log("======================");
      
      if (isEdit) {
        const updatePayload = {
          id: initial!.id,
          ...payload,
        };
        console.log("Update payload:", updatePayload);
        await onUpdate(updatePayload as any);
      } else {
        console.log("Create payload:", payload);
        await onCreate(payload as any);
      }
      await onSaved();
      onOpenChange(false);
    } catch (e: any) {
      console.error("=== SAVE ERROR ===");
      console.error("Error object:", e);
      console.error("Error message:", e?.message);
      console.error("Error data:", e?.data);
      console.error("==================");
      alert(e?.message || "Failed to save template");
    }
  };

  const disabled = !name || !androidTitle || !androidBody || !iosTitle || !iosBody;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] lg:max-w-[1400px] rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit template" : "New template"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Template Name */}
          <div>
            <Label className="mb-1 block">Template Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Welcome Message"
              className="rounded-xl"
            />
          </div>

          {/* Android & iOS side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Android Section */}
            <div className="rounded-xl border p-4 space-y-4 bg-green-50/50">
              <div className="flex items-center gap-2">
                <div className="font-medium text-base">Android Notification</div>
                <div className="px-2 py-0.5 rounded-full bg-green-600 text-white text-xs">Android</div>
              </div>
              
              <div>
                <Label className="mb-1 block">Title *</Label>
                <Input
                  value={androidTitle}
                  onChange={(e) => setAndroidTitle(e.target.value)}
                  placeholder="Notification title"
                  className="rounded-xl"
                />
              </div>

              <div>
                <Label className="mb-1 block">Body *</Label>
                <Textarea
                  value={androidBody}
                  onChange={(e) => setAndroidBody(e.target.value)}
                  placeholder="Notification message"
                  rows={4}
                  className="rounded-xl"
                />
              </div>
            </div>

            {/* iOS Section */}
            <div className="rounded-xl border p-4 space-y-4 bg-blue-50/50">
              <div className="flex items-center gap-2">
                <div className="font-medium text-base">iOS Notification</div>
                <div className="px-2 py-0.5 rounded-full bg-blue-600 text-white text-xs">iOS</div>
              </div>
              
              <div>
                <Label className="mb-1 block">Title *</Label>
                <Input
                  value={iosTitle}
                  onChange={(e) => setIosTitle(e.target.value)}
                  placeholder="Notification title"
                  className="rounded-xl"
                />
              </div>

              <div>
                <Label className="mb-1 block">Subtitle (optional)</Label>
                <Input
                  value={iosSubtitle}
                  onChange={(e) => setIosSubtitle(e.target.value)}
                  placeholder="Notification subtitle"
                  className="rounded-xl"
                />
              </div>

              <div>
                <Label className="mb-1 block">Body *</Label>
                <Textarea
                  value={iosBody}
                  onChange={(e) => setIosBody(e.target.value)}
                  placeholder="Notification message"
                  rows={4}
                  className="rounded-xl"
                />
              </div>
            </div>
          </div>

          {/* Shared Image */}
          <div>
            <Label className="mb-1 block">Image URL (optional - shared for both platforms)</Label>
            <Input
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://cdn.example.com/image.png"
              className="rounded-xl"
            />
            <div className="mt-1 text-xs text-muted-foreground">
              Use a public HTTPS URL so devices can fetch the image in the notification.
            </div>
          </div>

          {/* Preview Section */}
          <div className="rounded-xl border p-4 bg-muted/30">
            <div className="font-medium text-sm mb-4">Live Previews</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Android Preview */}
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground text-center">Android Preview</div>
                <div className="flex justify-center">
                  <div className="w-full max-w-[340px] rounded-2xl border bg-white p-3 shadow-lg">
                    <div className="flex items-center gap-2 mb-2 px-1">
                      <div className="flex h-5 w-5 items-center justify-center rounded bg-green-600 text-white text-[10px] font-bold">
                        A
                      </div>
                      <div className="text-xs font-medium">Your App</div>
                      <div className="ml-auto text-[10px] text-muted-foreground">now</div>
                    </div>
                    <div className="flex gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold leading-tight">
                          {androidTitle || <span className="text-muted-foreground">(title)</span>}
                        </div>
                        <div className="mt-1 text-xs leading-snug text-muted-foreground line-clamp-2">
                          {androidBody || <span>(message)</span>}
                        </div>
                      </div>
                      {imageUrl && (
                        <div className="shrink-0">
                          <img src={imageUrl} alt="preview" className="h-12 w-12 rounded object-cover" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* iOS Preview */}
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground text-center">iOS Preview</div>
                <div className="flex justify-center">
                  <PhonePreviewIOS
                    appName="Your App"
                    header={iosTitle}
                    subtitle={iosSubtitle}
                    body={iosBody}
                    link={undefined}
                    image={imageUrl ? { name: "template-image", dataUrl: imageUrl } : null}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-xl"
          >
            Cancel
          </Button>
          <Button disabled={disabled} onClick={doSave} className="rounded-xl">
            <Save className="mr-2 h-4 w-4" /> Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}